import React from "react";

/* CSS-rendered 3D plush/clay shapes.
   Each shape is a styled <div> with layered radial gradients + inner highlights
   to simulate volume — inspired by Intelly-style claymorphic stickers. */

const clayBase = (color, highlight, shadow) => ({
  background: `radial-gradient(circle at 30% 28%, ${highlight} 0%, ${color} 45%, ${shadow} 100%)`,
  boxShadow: `inset -6px -8px 16px ${shadow}33, inset 6px 8px 14px ${highlight}66, 0 14px 28px -10px rgba(60,30,20,.35)`,
});

/* HEART — plush rounded heart using SVG + radial gradient fill (clean shape) */
export const HeartSticker = ({ size = 60, color = "#E58FA8", className = "", style }) => {
  const id = React.useId();
  const gradId = `heart-grad-${id}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      style={{
        filter: `drop-shadow(0 8px 18px rgba(60,30,20,0.30))`,
        ...style,
      }}
      aria-hidden
    >
      <defs>
        <radialGradient id={gradId} cx="34%" cy="28%" r="70%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
          <stop offset="35%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.18" />
        </radialGradient>
      </defs>
      <path
        d="M50 86 C 22 70, 8 50, 8 32 C 8 19, 20 9, 32 9 C 41 9, 47 14, 50 22 C 53 14, 59 9, 68 9 C 80 9, 92 19, 92 32 C 92 50, 78 70, 50 86 Z"
        fill={`url(#${gradId})`}
      />
    </svg>
  );
};

/* STAR — plush rounded sparkle/star burst */
export const StarSticker = ({ size = 50, color = "#E9B86A", className = "", style }) => {
  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-hidden
    >
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 30% 28%, #FFFFFF 0%, ${color} 50%, #00000022 100%)`,
          clipPath: "polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)",
          boxShadow: `inset -3px -4px 10px rgba(0,0,0,0.18)`,
        }}
      />
    </div>
  );
};

/* CRESCENT — plush crescent moon */
export const CrescentSticker = ({ size = 56, color = "#A8C0E8", className = "", style }) => {
  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-hidden
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{
          ...clayBase(color, "#FFFFFF", "#000000"),
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          width: size * 0.78, height: size * 0.78,
          right: -size * 0.16, top: -size * 0.05,
          background: "hsl(36 42% 94%)",
          boxShadow: `inset 4px 4px 12px rgba(0,0,0,0.18)`,
        }}
      />
    </div>
  );
};

/* BLOB — organic plush blob (use as floating accent) */
export const BlobSticker = ({ size = 120, color = "#B8D4BD", className = "", style }) => {
  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-hidden
    >
      <div
        className="absolute inset-0"
        style={{
          borderRadius: "58% 42% 64% 36% / 50% 60% 40% 50%",
          ...clayBase(color, "#FFFFFF", "#000000"),
        }}
      />
    </div>
  );
};

/* FLOWER — plush petal cluster */
export const FlowerSticker = ({ size = 56, color = "#E5A8C0", className = "", style }) => {
  const petal = (rot) => (
    <div
      className="absolute rounded-full"
      style={{
        width: size * 0.42, height: size * 0.42,
        top: size * 0.04, left: size * 0.29,
        transform: `rotate(${rot}deg) translateY(-${size * 0.22}px)`,
        transformOrigin: `${size * 0.21}px ${size * 0.46}px`,
        ...clayBase(color, "#FFFFFF", "#000000"),
      }}
    />
  );
  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-hidden
    >
      {petal(0)}
      {petal(72)}
      {petal(144)}
      {petal(216)}
      {petal(288)}
      <div
        className="absolute rounded-full"
        style={{
          width: size * 0.34, height: size * 0.34,
          left: size * 0.33, top: size * 0.33,
          ...clayBase("#F5E3C2", "#FFFFFF", "#000000"),
        }}
      />
    </div>
  );
};

/* SPARK BURST — plush diamond sparkle */
export const SparkBurst = ({ size = 44, color = "#E9B86A", className = "", style }) => {
  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-hidden
    >
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 30% 28%, #FFFFFF 0%, ${color} 55%, #00000022 100%)`,
          clipPath: "polygon(50% 0, 58% 42%, 100% 50%, 58% 58%, 50% 100%, 42% 58%, 0 50%, 42% 42%)",
          boxShadow: `inset -3px -4px 10px rgba(0,0,0,0.18)`,
        }}
      />
    </div>
  );
};

/* RING — plush ring/donut */
export const RingSticker = ({ size = 56, color = "#A8C0E8", className = "", style }) => {
  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-hidden
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{
          ...clayBase(color, "#FFFFFF", "#000000"),
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          inset: size * 0.28,
          background: "hsl(36 42% 94%)",
          boxShadow: `inset 3px 3px 8px rgba(0,0,0,0.18)`,
        }}
      />
    </div>
  );
};

/* PILL — plush horizontal pill shape (small accent) */
export const PillSticker = ({ size = 60, color = "#D5B8E8", className = "", style }) => {
  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size * 0.5, ...style }}
      aria-hidden
    >
      <div
        className="absolute inset-0"
        style={{
          borderRadius: "9999px",
          ...clayBase(color, "#FFFFFF", "#000000"),
        }}
      />
    </div>
  );
};
